import { useState, useEffect } from 'react';
import Header from '../../components/base/Header';
import Footer from '../../components/base/Footer';
import { Link } from 'react-router-dom';

interface Essay {
  id: number;
  title: string;
  content: string;
  date: string;
  author: string;
  tags: string[];
}

interface BlogPost {
  id: number;
  title: string;
  content: string;
  date: string;
  image: string;
  tags: string[];
  slug: string;
}

export default function EssayPage() {
  // ユーザーが作成したエッセイ（現時点では表示に使用していないが将来の拡張のために保持）
  const [userEssays] = useState<Essay[]>([
    {
      id: 1,
      title: '春の庭で見つけた小さな幸せ',
      content: '今朝、庭に出ると、昨日まで蕾だったチューリップが一輪咲いていました。淡いピンク色の花びらが朝日に照らされて、まるで宝石のように輝いていました。毎日水をやり、話しかけていた花が咲いた瞬間は、何度経験しても心が躍ります。\n\n植物を育てることは、待つことを学ぶことでもあります。すぐには結果が出ない。でも、毎日少しずつ変化していく姿を見守ることで、私たちも一緒に成長していくのかもしれません。\n\n花が咲くまでの時間は、決して無駄ではありません。土の中で根を張り、茎を伸ばし、蕾を膨らませる。その全てのプロセスが、美しい花を咲かせるために必要なのです。',
      date: '2024年3月15日',
      author: '花子',
      tags: ['春', 'チューリップ', '日常', '成長']
    },
    {
      id: 2,
      title: '雨の日の植物たち',
      content: '雨の日は外に出るのが億劫になりがちですが、植物たちにとっては恵みの日です。窓から見える庭の緑が、雨に濡れて一層鮮やかに見えます。\n\n特に好きなのは、雨粒が葉の上で転がる様子です。まるで小さな水晶玉のように、葉の上をコロコロと転がっていきます。自然が作り出す芸術作品を、無料で鑑賞できる贅沢な時間です。\n\n植物は雨を待っています。私たちが水やりを忘れても、自然が代わりに世話をしてくれる。そんな自然のサイクルの中に生きていることを、雨の日は思い出させてくれます。',
      date: '2024年3月10日',
      author: '太郎',
      tags: ['雨', '自然', '観察', '日常']
    },
    {
      id: 3,
      title: 'おばあちゃんの庭の記憶',
      content: '子供の頃、おばあちゃんの家には大きな庭がありました。そこには季節ごとに様々な花が咲き、私はよくその庭で遊んでいました。\n\n特に印象に残っているのは、夏の朝顔です。おばあちゃんと一緒に種を植え、毎朝早起きして花が咲くのを見に行きました。青や紫、ピンクの花が次々と咲く様子は、子供心にも美しく、魔法のように感じられました。\n\n今、自分の庭で朝顔を育てています。種を植えるたびに、おばあちゃんとの思い出が蘇ります。植物は、時を超えて人と人をつなぐ不思議な力を持っているのかもしれません。',
      date: '2024年3月5日',
      author: '美咲',
      tags: ['思い出', '朝顔', '家族', '夏']
    },
    {
      id: 4,
      title: '多肉植物との暮らし',
      content: '一人暮らしを始めてから、多肉植物を育て始めました。水やりの頻度が少なくて済むので、忙しい私にはぴったりです。\n\n最初は小さな鉢を一つだけ買ったのですが、気づけば窓辺は多肉植物だらけになっていました。それぞれに個性があって、見ているだけで癒されます。\n\n特にお気に入りは、ぷっくりとした葉が可愛いハオルチアです。透明感のある葉が光に透けて、まるで宝石のよう。仕事で疲れて帰ってきても、この子たちを見ると心が和みます。植物は言葉を話さないけれど、確かに私の生活を豊かにしてくれています。',
      date: '2024年2月28日',
      author: '健太',
      tags: ['多肉植物', '一人暮らし', '癒し', 'インテリア']
    },
    {
      id: 5,
      title: '失敗から学んだこと',
      content: '植物を育て始めた頃、たくさんの失敗をしました。水をやりすぎると根腐れさせたり、日光不足で枯らしたり。その度に落ち込みましたが、今思えばそれも大切な経験でした。\n\n失敗するたびに、その植物について調べ、学びました。どんな環境を好むのか、くらいの水が必要なのか。一つ一つの植物に個性があることを知りました。\n\n今では、植物の様子を見れば、何が必要なのかが少しずつわかるようになってきました。葉の色、茎の張り、土の状態。植物は言葉で訴えることはできませんが、確かにサインを出しています。それを読み取れるようになったとき、植物との対話が始まったような気がしました。',
      date: '2024年2月20日',
      author: '由美',
      tags: ['失敗', '学び', '成長', '経験']
    },
    {
      id: 6,
      title: 'ベランダガーデニングの楽しみ',
      content: 'マンション暮らしの私にとって、ベランダは小さな庭です。限られたスペースですが、工夫次第で素敵な緑の空間を作ることができます。\n\n縦の空間を活用して、ハンギングプランターを吊りしたり、棚を設置したり。小さなスペースでも、たくさんの植物を育てることができます。\n\n朝、コーヒーを片手にベランダに出て、植物たちに挨拶をする時間が一日の始まりです。都会の喧騒の中でも、ここだけは私だけの静かな楽園。植物たちと過ごす時間が、忙しい日々の中での大切なリセットタイムになっています。',
      date: '2024年2月15日',
      author: '翔太',
      tags: ['ベランダ', 'ガーデニング', '都会', '工夫']
    }
  ]);

  const [blogPosts] = useState<BlogPost[]>([
    {
      id: 7,
      title: '季節の花の楽しみ方：紅葉編',
      content: 'わんわん！秋の訪れを告げる、自然の芸術について紹介するよ🐾',
      date: '2025年11月15日',
      image: 'https://static.readdy.ai/image/8ca6952e6a33257e04fb8ac67bcd75fe/7075f9ca8c66547c2d43f2c6f9c4d865.png',
      tags: ['紅葉', '秋の風物詩', '蘭ちゃん'],
      slug: 'autumn-koyo'
    },
    {
      id: 6,
      title: '【第3弾】季節の花の楽しみ方：クリスマスローズ編',
      content: 'わんわん！冬の貴婦人と呼ばれる、優美な花について紹介するよ🐾',
      date: '2025年11月6日',
      image: 'https://static.readdy.ai/image/8ca6952e6a33257e04fb8ac67bcd75fe/3a83070bed90e40fafef6bc6f386223e.jpeg',
      tags: ['クリスマスローズ', '冬の花', '蘭ラ々'],
      slug: 'winter-christmas-rose'
    },
    {
      id: 5,
      title: '【第5弾】季節の花の楽しみ方：ポインセチア編',
      content: 'クリスマスを彩る、気品ある赤い花の魅力をご紹介いたします。',
      date: '2025年11月5日',
      image: 'https://static.readdy.ai/image/8ca6952e6a33257e04fb8ac67bcd75fe/cf1c35499a6d82530cf5d4aad867c56a.jpeg',
      tags: ['ポインセチア', 'クリスマス', 'アルバート'],
      slug: 'winter-poinsettia'
    },
    {
      id: 4,
      title: '今日の誕生花を探す機能について',
      content: '──あなたの大切な日に、特別な一輪を──',
      date: '2025年11月4日',
      image: 'https://static.readdy.ai/image/8ca6952e6a33257e04fb8ac67bcd75fe/7eed25f69c286b4cab5e9f9ba2948ad4.jpeg',
      tags: ['誕生花', '機能紹介', 'ティランちゃん'],
      slug: 'birth-flower-guide'
    },
    {
      id: 3,
      title: '【第1弾】季節の花の楽しみ方：冬編（シクラメン）',
      content: '──シクラメンと過ごす、あたたかな冬──',
      date: '2025年1月3日',
      image: 'https://static.readdy.ai/image/8ca6952e6a33257e04fb8ac67bcd75fe/eac34ebd96228ec8009c787c18aad611.jpeg',
      tags: ['冬', 'シクラメン', '季節の花'],
      slug: 'winter-cyclamen'
    },
    {
      id: 2,
      title: '🌼 今日の"花言葉おみくじ"ってなに？',
      content: '──あなたの一日に、やさしい一輪を──',
      date: '2025年11月2日',
      image: 'https://static.readdy.ai/image/8ca6952e6a33257e04fb8ac67bcd75fe/775a1306757aaaabc608ab540981a1c8.jpeg',
      tags: ['花言葉', 'おみくじ', 'コンセプト'],
      slug: 'omikuji-concept'
    },
    {
      id: 1,
      title: '【初投稿】ようこそ、モモちゃん畑へ。',
      content: '──花と心がやさしくつながる場所──',
      date: '2025年11月1日',
      image: 'https://static.readdy.ai/image/8ca6952e6a33257e04fb8ac67bcd75fe/8a7dae729d6b76b47b73c1239dd423a0.jpeg',
      tags: ['初投稿', 'コンセプト', 'モモちゃん畑'],
      slug: 'welcome'
    }
  ]);

  const [newEssay, setNewEssay] = useState({
    title: '',
    content: '',
    author: '',
    tags: ''
  });

  const [showForm, setShowForm] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // ここでエッセイを保存する処理を実装
    alert('エッセイが投稿されました！（実際の保存機能は今後実装予定です）');
    setNewEssay({ title: '', content: '', author: '', tags: '' });
    setShowForm(false);
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // 全記事データ（日付順）
  const allArticles = [
    {
      id: 8,
      title: "🌼 花屋めぐりシリーズ【第1弾】オザキフラワーパークに行ってきました！",
      excerpt: "息子と一緒に、記念すべき花屋めぐりの第1弾！今までで最大規模のオザキフラワーパークへ行ってきました。",
      image: "https://static.readdy.ai/image/8ca6952e6a33257e04fb8ac67bcd75fe/a9e025037dc3745007cfadb6273b94dc.png",
      date: "2025年11月16日",
      category: "花屋めぐり",
      path: "/blog/flower-shop-visit",
      featured: true
    },
    {
      id: 7,
      title: "季節の花の楽しみ方：紅葉編",
      excerpt: "美しい紅葉の季節がやってきました。ランちゃんが紅葉のメカニズムから楽しみ方まで詳しく解説します。",
      image: "https://static.readdy.ai/image/8ca6952e6a33257e04fb8ac67bcd75fe/7075f9ca8c66547c2d43f2c6f9c4d865.png",
      date: "2025年11月15日",
      category: "季節の花",
      path: "/blog/autumn-koyo",
      featured: true
    },
    {
      id: 6,
      title: "【第6弾】冬の花の代表格：ポインセチア",
      excerpt: "クリスマスシーズンに欠かせない美しい赤い花、ポインセチア。その魅力と育て方のコツをお教えします。",
      image: "https://public.readdy.ai/ai/img_res/b32bb8b4e746571f517d468b60f8c2d9.jpg",
      date: "2025年11月14日",
      category: "季節の花",
      path: "/blog/winter-poinsettia",
      featured: true
    },
    {
      id: 5,
      title: "【第5弾】冬の優雅な花：シクラメン",
      excerpt: "冬の室内を彩る美しいシクラメン。その特徴と上手な育て方のコツをお教えします。",
      image: "https://readdy.ai/api/search-image?query=elegant%20pink%20cyclamen%20flowers%20in%20terracotta%20pot%2C%20delicate%20petals%2C%20heart-shaped%20leaves%2C%20soft%20natural%20lighting%2C%20indoor%20plant%20photography%2C%20beautiful%20winter%20blooming%20plant%2C%20cozy%20home%20atmosphere&width=600&height=400&seq=cyclamen2025&orientation=landscape",
      date: "2025年11月13日",
      category: "季節の花",
      path: "/blog/winter-cyclamen",
      featured: true
    },
    {
      id: 4,
      title: "【第4弾】冬の貴婦人：クリスマスローズ",
      excerpt: "「冬の貴婦人」と呼ばれる美しいクリスマスローズ。その魅力と育て方のポイントをご紹介します。",
      image: "https://readdy.ai/api/search-image?query=elegant%20white%20Christmas%20rose%20hellebore%20flowers%20blooming%20in%20winter%20garden%2C%20delicate%20petals%2C%20dark%20green%20foliage%2C%20soft%20morning%20light%2C%20refined%20garden%20photography%2C%20peaceful%20winter%20scene%2C%20noble%20flower%20beauty&width=600&height=400&seq=christmas-rose2025&orientation=landscape",
      date: "2025年11月12日",
      category: "季節の花",
      path: "/blog/winter-christmas-rose",
      featured: true
    },
    {
      id: 3,
      title: "【第3弾】誕生花ガイドの使い方",
      excerpt: "毎日の誕生花を楽しく学べるガイドの使い方をご紹介。あなたの誕生花を見つけてみませんか？",
      image: "https://readdy.ai/api/search-image?query=colorful%20birthday%20flowers%20calendar%20guide%20book%2C%20beautiful%20floral%20arrangement%2C%20educational%20botanical%20illustration%2C%20soft%20pastel%20colors%2C%20elegant%20typography%2C%20inspiring%20learning%20atmosphere%2C%20artistic%20flower%20photography&width=600&height=400&seq=birth-flower-guide&orientation=landscape",
      date: "2025年11月11日",
      category: "ガイド",
      path: "/blog/birth-flower-guide",
      featured: false
    },
    {
      id: 2,
      title: "【第2弾】おみくじの概念について",
      excerpt: "植物おみくじに込められた想いと、運勢の考え方について詳しく解説します。",
      image: "https://readdy.ai/api/search-image?query=traditional%20Japanese%20omikuji%20fortune%20telling%20with%20beautiful%20flowers%2C%20botanical%20divination%20concept%2C%20peaceful%20zen%20garden%20setting%2C%20soft%20natural%20lighting%2C%20cultural%20harmony%20between%20nature%20and%20spirituality%2C%20elegant%20minimalist%20design&width=600&height=400&seq=omikuji-concept&orientation=landscape",
      date: "2025年11月10日",
      category: "文化",
      path: "/blog/omikuji-concept",
      featured: false
    },
    {
      id: 1,
      title: "【第1弾】ももちゃんの花ブログへようこそ！",
      excerpt: "花と植物に関する様々な情報をお届けする、ももちゃんのブログがスタートしました！",
      image: "https://readdy.ai/api/search-image?query=cheerful%20anime%20girl%20character%20with%20pink%20hair%20and%20sakura%20hair%20ornament%20in%20beautiful%20flower%20garden%2C%20kawaii%20style%2C%20surrounded%20by%20colorful%20blooming%20flowers%2C%20warm%20spring%20sunlight%2C%20joyful%20welcoming%20atmosphere%2C%20cute%20botanical%20illustration&width=600&height=400&seq=momo-welcome&orientation=landscape",
      date: "2025年11月9日",
      category: "お知らせ",
      path: "/blog/welcome",
      featured: false
    }
  ];

  // 最新5件と過去記事に分ける
  const latestArticles = allArticles.slice(0, 5);
  const pastArticles = allArticles.slice(5);

  const allBlogPosts = [
    {
      id: 'stress-memory',
      title: '植物はストレスを記憶するのか？',
      excerpt: '沈黙の中で積み重ねられる「経験」──植物はエピジェネティック変化を通じて過去のストレス経験を記憶し、次の危機に備えています。',
      date: '2026年1月26日',
      author: 'ももちゃん',
      category: '植物科学',
      image: 'https://readdy.ai/api/search-image?query=scientific%20illustration%20of%20plant%20epigenetic%20memory%20and%20stress%20response%2C%20glowing%20DNA%20methylation%20patterns%20in%20plant%20cells%2C%20mystical%20visualization%20of%20cellular%20memory%20formation%2C%20educational%20botanical%20diagram%20showing%20histone%20modification%20and%20gene%20priming%2C%20soft%20rose%20and%20pink%20lighting%2C%20peaceful%20natural%20environment%20demonstrating%20invisible%20molecular%20changes%20that%20enable%20plants%20to%20remember%20past%20stress%20experiences&width=800&height=600&seq=stress-memory-epigenetics&orientation=landscape',
      gradient: 'from-rose-400 to-pink-500',
      link: '/blog/stress-memory'
    },
    {
      id: 'calling-predators',
      title: '植物は天敵を呼ぶ',
      excerpt: '自分で戦わず、「助け」を使うという戦略──植物は揮発性有機化合物を放出し、害虫の天敵を呼び寄せる間接防衛を行っています。',
      date: '2026年1月19日',
      author: 'ももちゃん',
      category: '植物科学',
      image: 'https://readdy.ai/api/search-image?query=scientific%20illustration%20of%20plant%20releasing%20volatile%20organic%20compounds%20to%20attract%20predatory%20insects%2C%20glowing%20green%20chemical%20signals%20floating%20in%20air%20toward%20beneficial%20insects%20like%20parasitic%20wasps%20and%20predatory%20mites%2C%20educational%20botanical%20diagram%20showing%20indirect%20plant%20defense%20mechanism%2C%20peaceful%20natural%20environment%20with%20soft%20emerald%20and%20jade%20green%20lighting%2C%20mystical%20atmosphere%20demonstrating%20plant-insect-predator%20interaction%20triangle&width=800&height=600&seq=calling-predators-vocs&orientation=landscape',
      gradient: 'from-green-400 to-emerald-500',
      link: '/blog/calling-predators'
    },
    {
      id: 'insect-recognition',
      title: '植物は昆虫を「識別」している',
      excerpt: '同じ傷でも、反応はまったく違う──植物は昆虫の唾液中の化学物質を検知し、相手を見極めて最適な防御戦略を選んでいます。',
      date: '2026年1月12日',
      author: 'ももちゃん',
      category: '植物科学',
      image: 'https://readdy.ai/api/search-image?query=scientific%20illustration%20of%20plant%20leaf%20being%20eaten%20by%20insect%2C%20showing%20chemical%20detection%20of%20insect%20saliva%20elicitors%2C%20glowing%20molecular%20signals%20and%20plant%20defense%20response%20activation%2C%20educational%20botanical%20diagram%20with%20warm%20amber%20and%20orange%20lighting%2C%20peaceful%20natural%20environment%20demonstrating%20plant%20recognition%20of%20herbivore%20attack%20versus%20mechanical%20damage&width=800&height=600&seq=insect-recognition-elicitor&orientation=landscape',
      gradient: 'from-amber-400 to-orange-500',
      link: '/blog/insect-recognition'
    },
    {
      id: 'internal-communication',
      title: '植物体内の会話',
      excerpt: '電気信号とCa²⁺波──植物の体内では、秒単位で全身を駆け巡る情報伝達システムが存在しています。',
      date: '2026年1月5日',
      author: 'ももちゃん',
      category: '植物科学',
      image: 'https://readdy.ai/api/search-image?query=scientific%20illustration%20of%20electrical%20signals%20and%20calcium%20ion%20waves%20traveling%20through%20plant%20cells%2C%20glowing%20purple%20and%20violet%20bioelectric%20currents%20flowing%20through%20plant%20tissue%2C%20mystical%20bioluminescent%20visualization%20of%20internal%20plant%20communication%2C%20educational%20botanical%20diagram%20style%20with%20soft%20violet%20and%20lavender%20lighting%2C%20peaceful%20natural%20environment%20showing%20invisible%20cellular%20signaling%20network&width=800&height=600&seq=internal-plant-signals&orientation=landscape',
      gradient: 'from-purple-400 to-violet-500',
      link: '/blog/internal-communication'
    },
    {
      id: 'air-communication',
      title: '空気中の会話',
      excerpt: '揮発性有機化合物という沈黙の言語──植物は匂いを使って危険を知らせ、仲間を守るための情報を伝えています。',
      date: '2026年12月22日',
      author: 'ももちゃん',
      category: '植物科学',
      image: 'https://readdy.ai/api/search-image?query=scientific%20illustration%20of%20plants%20releasing%20volatile%20organic%20compounds%20into%20air%2C%20glowing%20green%20chemical%20molecules%20floating%20between%20plants%2C%20mystical%20forest%20atmosphere%20with%20bioluminescent%20elements%20showing%20plant%20communication%20through%20air%2C%20educational%20botanical%20diagram%20style%20with%20soft%20emerald%20and%20teal%20lighting%2C%20peaceful%20natural%20environment%20demonstrating%20invisible%20chemical%20signals%20and%20plant%20defense%20mechanisms&width=800&height=600&seq=air-communication-vocs&orientation=landscape',
      gradient: 'from-emerald-400 to-teal-500',
      link: '/blog/air-communication'
    },
    {
      id: 'plant-communication',
      title: '植物は本当に「会話」しているのか？',
      excerpt: '沈黙の中で交わされる、生物学的コミュニケーション──植物が行う情報のやり取りを、科学的な視点から解説します。',
      date: '2026年12月15日',
      author: 'ももちゃん',
      category: '植物科学',
      image: 'https://readdy.ai/api/search-image?query=scientific%20illustration%20of%20plants%20communicating%20through%20underground%20root%20networks%20and%20chemical%20signals%2C%20glowing%20connections%20between%20plants%2C%20mystical%20forest%20atmosphere%20with%20bioluminescent%20elements%2C%20educational%20botanical%20diagram%20style%2C%20soft%20green%20and%20blue%20lighting%2C%20peaceful%20natural%20environment%20showing%20invisible%20plant%20communication&width=800&height=600&seq=plant-communication-science&orientation=landscape',
      gradient: 'from-emerald-400 to-teal-500',
      link: '/blog/plant-communication'
    },
    {
      id: 'naturalist-visit',
      title: '🌿 花屋めぐりシリーズ【第2弾】The Naturalist に行ってきた',
      excerpt: '植物と静かに向き合える空間──上尾市のThe Naturalistで出会った、エアープランツの魅力をお届けします。',
      date: '2026年12月7日',
      author: 'ももちゃん',
      category: '花屋めぐり',
      image: 'https://static.readdy.ai/image/8ca6952e6a33257e04fb8ac67bcd75fe/308f38f6df35766a6495e648d0c30acd.png',
      gradient: 'from-teal-400 to-emerald-500',
      link: '/blog/naturalist-visit'
    },
    {
      id: 'underground-network',
      title: '地下の会話：菌根菌ネットワーク',
      excerpt: '見えない場所で、森はつながっている。菌根菌ネットワークを介して、植物たちは炭素や防御シグナル、ストレス情報を共有しています。',
      date: '2025年12月29日',
      author: 'ももちゃん',
      category: '植物科学',
      image: 'https://readdy.ai/api/search-image?query=scientific%20illustration%20of%20underground%20mycorrhizal%20fungal%20network%20connecting%20tree%20roots%2C%20glowing%20mycelium%20threads%20between%20plants%2C%20mystical%20underground%20forest%20ecosystem%20with%20bioluminescent%20elements%2C%20educational%20botanical%20diagram%20showing%20wood%20wide%20web%2C%20soft%20amber%20and%20emerald%20lighting%2C%20peaceful%20natural%20environment%20demonstrating%20invisible%20underground%20plant%20communication%20and%20nutrient%20sharing&width=800&height=600&seq=underground-mycorrhizal-network&orientation=landscape',
      gradient: 'from-emerald-400 to-teal-500',
      link: '/blog/underground-network'
    },
    {
      id: 'flower-shop-visit',
      title: '🌼 花屋めぐりシリーズ【第1弾】オザキフラワーパークに行ってきました！',
      excerpt: '息子と一緒に花の楽園を大冒険──記念すべき第1弾は今までで最大規模の花屋さんに挑戦です！',
      date: '2025年11月30日',
      author: 'ももちゃん',
      category: '花屋めぐり',
      image: 'https://static.readdy.ai/image/8ca6952e6a33257e04fb8ac67bcd75fe/a9e025037dc3745007cfadb6273b94dc.png',
      gradient: 'from-green-400 to-emerald-500',
      link: '/blog/flower-shop-visit'
    },
    {
      id: 'autumn-koyo',
      title: '季節の花の楽しみ方：紅葉編',
      excerpt: '秋の訪れを告げる、自然の芸術。紅葉のメカニズムから楽しみ方まで、ランちゃんがご紹介します。',
      date: '2025年11月15日',
      author: 'ランちゃん',
      category: '季節の花',
      image: 'https://static.readdy.ai/image/8ca6952e6a33257e04fb8ac67bcd75fe/7075f9ca8c66547c2d43f2c6f9c4d865.png',
      gradient: 'from-orange-400 to-red-500',
      link: '/blog/autumn-koyo'
    },
    {
      id: 'winter-christmas-rose',
      title: '【第6弾】冬の花の楽しみ方：クリスマスローズ編',
      excerpt: '冬の貴婦人と呼ばれるクリスマスローズの魅力と楽しみ方を、ランちゃんがご紹介します。',
      date: '2025年11月10日',
      author: 'ランちゃん',
      category: '季節の花',
      image: 'https://readdy.ai/api/search-image?query=elegant%20Christmas%20rose%20flowers%20blooming%20in%20winter%20garden%20with%20soft%20natural%20lighting%20and%20dreamy%20bokeh%20background%2C%20beautiful%20white%20and%20pink%20petals%20with%20delicate%20details&width=800&height=600&seq=winter-christmas-rose-bloom&orientation=landscape',
      gradient: 'from-pink-400 to-rose-500',
      link: '/blog/winter-christmas-rose'
    },
    {
      id: 'winter-poinsettia',
      title: '【第2弾】冬の花の楽しみ方：ポインセチア編',
      excerpt: 'クリスマスの代表的な花、ポインセチアの美しさと育て方のコツを詳しくご紹介します。',
      date: '2025年11月8日',
      author: 'ランちゃん',
      category: '季節の花',
      image: 'https://readdy.ai/api/search-image?query=beautiful%20red%20poinsettia%20flowers%20in%20elegant%20Christmas%20decoration%20setting%20with%20warm%20lighting%20and%20festive%20atmosphere%2C%20vibrant%20red%20bracts%20with%20green%20foliage&width=800&height=600&seq=poinsettia-christmas-decor&orientation=landscape',
      gradient: 'from-red-400 to-pink-500',
      link: '/blog/winter-poinsettia'
    },
    {
      id: 'birth-flower-guide',
      title: '誕生花ガイド：あなたの誕生月の花を知ろう',
      excerpt: '12ヶ月それぞれの誕生花とその花言葉、特徴について詳しくご紹介します。',
      date: '2025年11月5日',
      author: 'ランちゃん',
      category: '花の知識',
      image: 'https://readdy.ai/api/search-image?query=beautiful%20collection%20of%20birth%20flowers%20arranged%20in%20elegant%20bouquet%20with%20soft%20pastel%20colors%20and%20dreamy%20lighting%2C%20representing%20twelve%20months%20of%20the%20year&width=800&height=600&seq=birth-flowers-collection&orientation=landscape',
      gradient: 'from-purple-400 to-pink-500',
      link: '/blog/birth-flower-guide'
    },
    {
      id: 'winter-cyclamen',
      title: '【第1弾】冬の花の楽しみ方：シクラメン編',
      excerpt: '冬の室内を彩る美しいシクラメン。その魅力と長く楽しむためのコツをご紹介します。',
      date: '2025年11月3日',
      author: 'ランちゃん',
      category: '季節の花',
      image: 'https://readdy.ai/api/search-image?query=beautiful%20cyclamen%20flowers%20in%20elegant%20pot%20arrangement%20with%20soft%20winter%20lighting%20and%20cozy%20indoor%20atmosphere%2C%20pink%20and%20white%20petals%20with%20heart-shaped%20leaves&width=800&height=600&seq=winter-cyclamen-indoor&orientation=landscape',
      gradient: 'from-pink-400 to-purple-500',
      link: '/blog/winter-cyclamen'
    },
    {
      id: 'omikuji-concept',
      title: '花みくじの世界へようこそ',
      excerpt: '花の力で心を癒し、新しい気づきを得る。花みくじの魅力と楽しみ方をご紹介します。',
      date: '2025年11月1日',
      author: 'ももちゃん',
      category: '花みくじ',
      image: 'https://readdy.ai/api/search-image?query=mystical%20flower%20fortune%20telling%20setup%20with%20beautiful%20blooming%20flowers%2C%20soft%20magical%20lighting%20and%20dreamy%20atmosphere%2C%20colorful%20petals%20arranged%20in%20spiritual%20pattern&width=800&height=600&seq=flower-fortune-mystical&orientation=landscape',
      gradient: 'from-purple-400 to-indigo-500',
      link: '/blog/omikuji-concept'
    },
    {
      id: 'welcome',
      title: 'モモちゃん畑へようこそ！',
      excerpt: 'はじめまして！ももちゃんです。このサイトで花と緑の魅力をお伝えしていきます。',
      date: '2025年10月30日',
      author: 'ももちゃん',
      category: 'ごあいさつ',
      image: 'https://readdy.ai/api/search-image?query=welcoming%20flower%20garden%20with%20cute%20character%20mascot%2C%20bright%20cheerful%20atmosphere%20with%20colorful%20blooming%20flowers%20and%20green%20plants%2C%20sunny%20day%20with%20warm%20lighting&width=800&height=600&seq=welcome-garden-mascot&orientation=landscape',
      gradient: 'from-green-400 to-teal-500',
      link: '/blog/welcome'
    }
  ];

  // 最新5件と過去記事に分ける
  const latestPosts = allBlogPosts.slice(0, 5);
  const pastPosts = allBlogPosts.slice(5);

  // 特集エッセイ（植物科学シリーズ）カード
  const plantEssays = [
    {
      id: 6,
      title: '植物は天敵を呼ぶ',
      excerpt: '自分で戦わず、「助け」を使うという戦略。植物は揮発性有機化合物を放出し、害虫の天敵を呼び寄せる間接防衛を行っています。',
      date: '2026年1月19日',
      category: '植物科学',
      icon: 'ri-bug-2-line',
      gradient: 'from-emerald-100 to-teal-100',
      iconColor: 'text-emerald-600',
      link: '/blog/calling-predators'
    },
    {
      id: 5,
      title: '植物は昆虫を「識別」している',
      excerpt: '同じ傷でも、反応はまったく違う。植物は昆虫の唾液中の化学物質を検知し、相手を見極めて最適な防御戦略を選んでいます。',
      date: '2026年1月12日',
      category: '植物科学',
      icon: 'ri-bug-line',
      gradient: 'from-amber-100 to-orange-100',
      iconColor: 'text-amber-600',
      link: '/blog/insect-recognition'
    },
    {
      id: 4,
      title: '植物体内の会話',
      excerpt: '電気信号とCa²⁺波。植物の体内では、秒単位で全身を駆け巡る情報伝達システムが存在しています。',
      date: '2026年1月5日',
      category: '植物科学',
      icon: 'ri-flashlight-line',
      gradient: 'from-purple-100 to-violet-100',
      iconColor: 'text-purple-600',
      link: '/blog/internal-communication'
    },
    {
      id: 2,
      title: '空気中の会話',
      excerpt: '揮発性有機化合物という沈黙の言語。植物は匂いを使って危険を知らせ、仲間を守るための情報を伝えています。',
      date: '2026年12月22日',
      category: '植物科学',
      icon: 'ri-windy-line',
      gradient: 'from-emerald-100 to-teal-100',
      iconColor: 'text-emerald-600',
      link: '/blog/air-communication'
    },
    {
      id: 3,
      title: '植物は本当に「会話」しているのか？',
      excerpt: '沈黙の中で交わされる、生物学的コミュニケーション。植物は情報を発し、受け取り、それに応じて行動を変えています。',
      date: '2026年12月15日',
      category: '植物科学',
      icon: 'ri-chat-3-line',
      gradient: 'from-emerald-100 to-teal-100',
      iconColor: 'text-emerald-600',
      link: '/blog/plant-communication'
    },
    {
      id: 1,
      title: '地下の会話：菌根菌ネットワーク',
      excerpt: '見えない場所で、森はつながっている。菌根菌ネットワークを介して、植物たちは炭素や防御シグナル、ストレス情報を共有しています。',
      date: '2025年12月29日',
      category: '植物科学',
      icon: 'ri-links-line',
      gradient: 'from-emerald-100 to-teal-100',
      iconColor: 'text-emerald-600',
      link: '/blog/underground-network'
    }
  ];

  const essays = [
    {
      id: 7,
      title: '植物はストレスを記憶するのか？',
      excerpt: '沈黙の中で積み重ねられる「経験」。植物の記憶とエピジェネティクス。',
      date: '2026年1月26日',
      category: '植物科学シリーズ',
      link: '/blog/stress-memory',
      gradient: 'from-rose-100 to-pink-100',
      icon: 'ri-brain-line',
      iconColor: 'text-rose-600'
    },
    {
      id: 6,
      title: '植物は天敵を呼ぶ',
      excerpt: '自分で戦わず、「助け」を使うという戦略。間接防衛のメカニズム。',
      date: '2026年1月19日',
      category: '植物科学シリーズ',
      link: '/blog/calling-predators',
      gradient: 'from-emerald-100 to-teal-100',
      icon: 'ri-plant-line',
      iconColor: 'text-emerald-600'
    },
    {
      id: 5,
      title: '植物は昆虫を「識別」している',
      excerpt: '機械的損傷と生物による攻撃を見分ける、植物の高度な認知能力。',
      date: '2026年1月12日',
      category: '植物科学シリーズ',
      link: '/blog/insect-recognition',
      gradient: 'from-amber-100 to-orange-100',
      icon: 'ri-bug-line',
      iconColor: 'text-amber-600'
    },
    {
      id: 4,
      title: '植物体内の会話',
      excerpt: '電気信号とCa²⁺（カルシウム）波による、植物の体内情報伝達システム。',
      date: '2026年1月5日',
      category: '植物科学シリーズ',
      link: '/blog/internal-communication',
      gradient: 'from-purple-100 to-violet-100',
      icon: 'ri-flashlight-line',
      iconColor: 'text-purple-600'
    },
    {
      id: 2,
      title: '空気中の会話',
      excerpt: '揮発性有機化合物という沈黙の言語。植物は匂いを使って危険を知らせ、仲間を守るための情報を伝えています。',
      date: '2026年12月22日',
      category: '植物科学',
      link: '/blog/air-communication',
      gradient: 'from-emerald-100 to-teal-100',
      icon: 'ri-windy-line',
      iconColor: 'text-emerald-600'
    },
    {
      id: 3,
      title: '植物は本当に「会話」しているのか？',
      excerpt: '沈黙の中で交わされる、生物学的コミュニケーション。植物は情報を発し、受け取り、それに応じて行動を変えています。',
      date: '2026年12月15日',
      category: '植物科学',
      link: '/blog/plant-communication',
      gradient: 'from-emerald-100 to-teal-100',
      icon: 'ri-chat-3-line',
      iconColor: 'text-emerald-600'
    },
    {
      id: 1,
      title: '地下の会話：菌根菌ネットワーク',
      excerpt: '見えない場所で、森はつながっている。菌根菌ネットワークを介して、植物たちは炭素や防御シグナル、ストレス情報を共有しています。',
      date: '2025年12月29日',
      category: '植物科学',
      link: '/blog/underground-network',
      gradient: 'from-emerald-100 to-teal-100',
      icon: 'ri-links-line',
      iconColor: 'text-emerald-600'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-rose-50 to-orange-50">
      <Header />

      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-r from-rose-500 via-pink-500 to-orange-500 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black/10"></div>
        <div className="container mx-auto px-4 relative">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-block mb-6">
              <div className="w-24 h-24 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm">
                <i className="ri-quill-pen-line text-6xl text-white"></i>
              </div>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              モモちゃんブログ
            </h1>
            <p className="text-xl md:text-2xl text-white/90 mb-8">
              花と緑の魅力をお伝えします
            </p>
            <div className="flex flex-wrap items-center justify-center gap-6 text-white/80">
              <span className="flex items-center text-lg">
                <i className="ri-leaf-line mr-2"></i>
                季節の花
              </span>
              <span className="flex items-center text-lg">
                <i className="ri-heart-line mr-2"></i>
                花言葉
              </span>
              <span className="flex items-center text-lg">
                <i className="ri-seedling-line mr-2"></i>
                育て方のコツ
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Latest Blog Posts */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-4xl font-bold text-gray-800 mb-12 text-center">
              <span className="bg-gradient-to-r from-rose-600 to-pink-600 bg-clip-text text-transparent">
                最新記事
              </span>
            </h2>
            
            <div className="space-y-8">
              {latestPosts.map((post) => (
                <Link
                  key={post.id}
                  to={post.link}
                  className="block group"
                >
                  <article className="bg-white rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 overflow-hidden border border-gray-100 hover:border-rose-200">
                    <div className="grid md:grid-cols-2 gap-0">
                      <div className="relative h-80 md:h-auto overflow-hidden">
                        <div className={`absolute inset-0 bg-gradient-to-br ${post.gradient} opacity-20`}></div>
                        <img
                          src={post.image}
                          alt={post.title}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                        />
                        <div className="absolute top-4 left-4">
                          <span className={`inline-block px-4 py-2 bg-gradient-to-r ${post.gradient} text-white text-sm font-bold rounded-full shadow-lg`}>
                            {post.category}
                          </span>
                        </div>
                      </div>
                      <div className="p-8 flex flex-col justify-center">
                        <div className="mb-4">
                          <div className="flex items-center gap-4 text-sm text-gray-600 mb-3">
                            <span className="flex items-center">
                              <i className="ri-calendar-line mr-1"></i>
                              {post.date}
                            </span>
                            <span className="flex items-center">
                              <i className="ri-user-line mr-1"></i>
                              {post.author}
                            </span>
                          </div>
                          <h3 className="text-2xl font-bold text-gray-800 mb-4 group-hover:text-rose-600 transition-colors">
                            {post.title}
                          </h3>
                          <p className="text-gray-600 leading-relaxed mb-6">
                            {post.excerpt}
                          </p>
                        </div>
                        <div className="flex items-center text-rose-600 opacity-0 group-hover:opacity-100 transition-opacity">
                          記事を読む
                          <i className="ri-arrow-right-line ml-2 group-hover:translate-x-1 transition-transform"></i>
                        </div>
                      </div>
                    </div>
                  </article>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Past Articles Section */}
      {pastPosts.length > 0 && (
        <section className="py-16 bg-gradient-to-br from-gray-50 to-slate-50">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold text-gray-800 mb-8 text-center">
                過去の記事
              </h2>
              
              <div className="space-y-4">
                {pastPosts.map((post) => (
                  <Link
                    key={post.id}
                    to={post.link}
                    className="block group"
                  >
                    <article className="bg-white rounded-2xl shadow-md hover:shadow-lg transition-all duration-300 p-6 border border-gray-100 hover:border-rose-200">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <span className="px-3 py-1 bg-gray-100 rounded-full text-sm font-medium text-gray-600">
                              {post.category}
                            </span>
                            <span className="text-sm text-gray-500">{post.date}</span>
                          </div>
                          <h3 className="text-lg font-bold text-gray-800 hover:text-rose-600 transition-colors line-clamp-2">
                            {post.title}
                          </h3>
                          <p className="text-gray-600 text-sm mt-2 line-clamp-2">
                            {post.excerpt}
                          </p>
                        </div>
                        <div className="flex items-center text-rose-600 opacity-0 group-hover:opacity-100 transition-opacity">
                          <i className="ri-arrow-right-line"></i>
                        </div>
                      </div>
                    </article>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Plant Science Essays Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-gray-800 mb-12 text-center">
            植物科学シリーズ
          </h2>
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            {plantEssays.slice(0, 3).map((item) => (
              <Link
                key={item.id}
                to={item.link}
                className="block group"
              >
                <article className="bg-white rounded-2xl shadow-md hover:shadow-lg transition-all duration-300 p-6 border border-gray-100 hover:border-emerald-200">
                  <div className="flex items-center mb-4">
                    <div className={`w-12 h-12 flex items-center justify-center rounded-full bg-gradient-to-br ${item.gradient} text-white`}>
                      <i className={`${item.icon} text-xl`}></i>
                    </div>
                    <span className="ml-3 text-sm text-gray-500">{item.date}</span>
                  </div>
                  <h3 className="text-lg font-bold text-gray-800 mb-2 group-hover:text-emerald-600 transition-colors">
                    {item.title}
                  </h3>
                  <p className="text-gray-600">{item.excerpt}</p>
                </article>
              </Link>
            ))}
          </div>
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {plantEssays.slice(3).map((item) => (
              <Link
                key={item.id}
                to={item.link}
                className="block group"
              >
                <article className="bg-white rounded-2xl shadow-md hover:shadow-lg transition-all duration-300 p-6 border border-gray-100 hover:border-emerald-200">
                  <div className="flex items-center mb-4">
                    <div className={`w-12 h-12 flex items-center justify-center rounded-full bg-gradient-to-br ${item.gradient} text-white`}>
                      <i className={`${item.icon} text-xl`}></i>
                    </div>
                    <span className="ml-3 text-sm text-gray-500">{item.date}</span>
                  </div>
                  <h3 className="text-lg font-bold text-gray-800 mb-2 group-hover:text-emerald-600 transition-colors">
                    {item.title}
                  </h3>
                  <p className="text-gray-600">{item.excerpt}</p>
                </article>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-rose-500 to-pink-500">
        <div className="container mx-auto px-4 text-center">
          <div className="max-w-2xl mx-auto">
            <h2 className="text-3xl font-bold text-white mb-6">
              花と緑の魅力をもっと知りたい方へ
            </h2>
            <p className="text-xl text-white/90 mb-8">
              季節の花情報や育て方のコツを定期的にお届けします
            </p>
            <Link
              to="/"
              className="inline-flex items-center bg-white text-rose-600 font-bold py-4 px-8 rounded-full hover:bg-gray-50 transition-all duration-300 transform hover:scale-105 shadow-lg whitespace-nowrap"
            >
              <i className="ri-home-line mr-2"></i>
              ホームに戻る
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}